import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AUTH_TOKEN_KEY, clearStoredToken, getMe, getStoredToken, isUnauthorized } from "./api";
import { resetSocket } from "./socket";
import type { AuthUser } from "./types";

/**
 * Accounts live in the sibling ch-watcher app, which is the only place a
 * session is created. This app never authenticates anyone: it takes a session
 * that app already issued, asks the backend who it belongs to, and otherwise
 * runs anonymously and read-only. There is deliberately no password form here.
 *
 * A session has to be carried across, because localStorage is per-origin — being
 * signed in over on ch-watcher does nothing for this app's storage. Two ways in,
 * both accepting the same token:
 *
 *   ?token=<jwt>   a link from ch-watcher. Stripped from the URL the moment it
 *                  is read, so it does not linger in history or leak through a
 *                  Referer header on the next request.
 *   adoptToken()   pasted by hand on the operator gate, which keeps the token
 *                  out of the URL entirely.
 */

export interface AuthState {
  user: AuthUser | null;
  authenticated: boolean;
  isAdmin: boolean;
  /** True only while an existing token is being resolved. */
  loading: boolean;
  signOut: () => void;
  /** Accepts a session minted by ch-watcher and re-resolves against it. */
  adoptToken: (token: string) => void;
}

const ANONYMOUS: AuthState = {
  user: null,
  authenticated: false,
  isAdmin: false,
  loading: false,
  signOut: () => undefined,
  adoptToken: () => undefined,
};

const AuthContext = createContext<AuthState>(ANONYMOUS);

/**
 * Pulls a handed-over token out of the URL and stores it, before anything reads
 * storage. Runs at module scope so a link lands signed in on first paint rather
 * than flashing the anonymous view.
 */
function consumeTokenFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const handed = url.searchParams.get("token");
  if (!handed) return;

  try {
    window.localStorage.setItem(AUTH_TOKEN_KEY, handed);
  } catch {
    // A browser refusing storage is a read-only session, not a crash.
  }
  url.searchParams.delete("token");
  window.history.replaceState({}, "", url.pathname + url.search + url.hash);
}

consumeTokenFromUrl();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Nothing to resolve without a token, so an anonymous visit never flashes a
  // loading state.
  const [loading, setLoading] = useState(() => getStoredToken() !== null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);

    getMe(controller.signal)
      .then((me) => {
        if (!active) return;
        setUser(me.authenticated ? me.user : null);
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted) return;
        // A rejected token is worth forgetting; a backend that is merely down
        // is not, so the token survives to be retried on the next load.
        if (isUnauthorized(err)) clearStoredToken();
        setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [attempt]);

  // ch-watcher signs in on the same origin, so a token can appear or vanish in
  // another tab while this app is open.
  useEffect(() => {
    const onStorage = (evt: StorageEvent) => {
      if (evt.key !== null && evt.key !== AUTH_TOKEN_KEY) return;
      resetSocket();
      setAttempt((n) => n + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const adoptToken = useCallback((token: string) => {

    const trimmed = token.trim();

    if (!trimmed) return;

    try {

      window.localStorage.setItem(AUTH_TOKEN_KEY, trimmed);

    } catch {

      return;

    }

    resetSocket();

    // Re-run the resolve effect rather than trusting the token blindly.

    setAttempt((n) => n + 1);

  }, []);


  const signOut = useCallback(() => {
    clearStoredToken();
    setUser(null);
    setLoading(false);
    // The token is only sent at handshake time, so the socket has to be redone.
    resetSocket();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      authenticated: user !== null,
      isAdmin: user?.role === "admin",
      loading,
      signOut,
      adoptToken,
    }),
    [user, loading, signOut, adoptToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
